// src/pages/Network.tsx
import {
  IonAvatar,
  IonBadge,
  IonButton,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonPopover,
  IonRefresher,
  IonRefresherContent,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonSkeletonText,
  IonSpinner,
  IonText,
  IonToolbar,
  useIonToast
} from '@ionic/react';
import {
  albumsOutline,
  businessOutline,
  chevronForward,
  cloudDownloadOutline,
  cloudUploadOutline,
  ellipsisVertical,
  filterOutline,
  linkOutline,
  mailOpenOutline,
  mailOutline,
  personAddOutline,
  refreshOutline,
  star,
  starOutline,
  timeOutline
} from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import TitleBar from '../components/TitleBar.tsx';
import { enhancedContacts, gmailAuth } from '../services/api.ts';
import { extractErrorMessage } from '../utils/errorUtils.ts';
import Import from './Import.tsx';
import './Network.css';

// Interface for Enhanced Contact from backend
interface EnhancedContact {
  id: string;
  name: string;
  title: string;
  company: string;
  location: string;
  type: 'recruiter' | 'connection';
  email?: string;
  link?: string;
  // Enhanced fields
  last_interaction?: string;
  total_emails: number;
  emails_received: number;
  emails_sent: number;
  response_rate: number;
  interest_level: number;
  notes?: string;
}

// Date formatting helper
const formatDate = (dateString?: string): string => {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

// Interest level stars component
const InterestLevel: React.FC<{ level: number }> = ({ level }) => {
  const maxStars = 5;
  const displayLevel = Math.min(Math.max(Math.round(level / 2), 0), maxStars);

  return (
    <div className="interest-level">
      {[...Array(maxStars)].map((_, i) => (
        <IonIcon
          key={i}
          icon={i < displayLevel ? star : starOutline}
          color={i < displayLevel ? "warning" : "medium"}
          style={{ fontSize: '14px', marginRight: '2px' }}
        />
      ))}
    </div>
  );
};

const Network: React.FC = () => {
  const [present] = useIonToast();
  const [showImport, setShowImport] = useState(false);
  const [segment, setSegment] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');
  const [contactsList, setContactsList] = useState<EnhancedContact[]>([]);
  const [allContacts, setAllContacts] = useState<EnhancedContact[]>([]); // Store all contacts for client-side filtering
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 10;
  const scrollRef = useRef<HTMLIonInfiniteScrollElement>(null);
  const [filterInterest, setFilterInterest] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState<string | null>(null);
  const [gmailStatusLoading, setGmailStatusLoading] = useState(true);
  const [showManagePopover, setShowManagePopover] = useState(false);
  const [popoverEvent, setPopoverEvent] = useState<any>(undefined);
  const [importMode, setImportMode] = useState<'quick' | 'gmail' | 'file'>('gmail');
  const [autoImporting, setAutoImporting] = useState(false);

  // Local storage key for contacts cache
  const CONTACTS_CACHE_KEY = 'network_contacts_cache';
  // No expiration - cache persists until manually refreshed or new contacts imported

  const fetchContacts = async (pageNum: number, search?: string, append: boolean = false) => {
    try {
      // Set appropriate loading state
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      // Check cache for first page load without search
      if (pageNum === 1 && !search && !append) {
        const cached = localStorage.getItem(CONTACTS_CACHE_KEY);
        if (cached) {
          const { data } = JSON.parse(cached);
          setAllContacts(data);
          setHasMore(data.length === pageSize);
          setLoading(false);
          return;
        }
      }

      // Always fetch ALL contacts (no type filter) - we filter client-side
      const params: any = {
        page: pageNum,
        size: pageSize,
        search: search || undefined,
        filter: {
          interest: filterInterest !== null ? filterInterest : undefined
        }
      };

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );

      const response = await Promise.race([
        enhancedContacts.getEnhancedContacts(params),
        timeoutPromise
      ]) as any;

      if (append) {
        setAllContacts(prev => [...prev, ...(response || [])]);
      } else {
        setAllContacts(response || []);

        // Cache first page results in localStorage (persists across sessions)
        if (pageNum === 1 && !search) {
          localStorage.setItem(CONTACTS_CACHE_KEY, JSON.stringify({
            data: response || []
          }));
        }
      }

      setHasMore((response || []).length === pageSize);
    } catch (error: any) {
      console.error('Failed to fetch contacts:', error);
      // Don't show error toast on first load, just show empty state
      if (pageNum > 1 || append) {
        present({
          message: error.message === 'Request timeout' ? 'Request timed out, please try again' : 'Failed to load contacts',
          duration: 2000,
          position: 'bottom',
          color: 'warning'
        });
      }
      // Only clear contacts on first page error, not on infinite scroll error
      if (!append && pageNum === 1) {
        setAllContacts([]);
        setContactsList([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial load - fetch contacts and check Gmail status when page mounts
  useEffect(() => {
    fetchContacts(1, searchText);
    checkGmailStatus();
  }, []); // Only run once on mount

  const checkGmailStatus = async () => {
    try {
      setGmailStatusLoading(true);
      const response = await gmailAuth.getStatus();
      setGmailConnected(response.connected);
      setConnectedEmail(response.email);
    } catch (error) {
      console.error('Error checking Gmail status:', error);
    } finally {
      setGmailStatusLoading(false);
    }
  };

  // Check for Gmail OAuth callback
  useEffect(() => {
    const checkGmailCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('gmail_connected') === 'true') {
        // Show loading immediately
        setAutoImporting(true);

        // Clean up URL first
        window.history.replaceState({}, '', window.location.pathname);

        try {
          // Update Gmail status
          await checkGmailStatus();

          present({
            message: 'Gmail connected! Importing contacts...',
            duration: 2000,
            color: 'success'
          });

          const response = await gmailAuth.importContacts();

          // Refresh contacts list
          handleContactAdded();

          present({
            message: `Successfully imported ${response.total_imported} contacts!`,
            duration: 3000,
            color: 'success'
          });
        } catch (error: any) {
          // Handle 402 errors specially - don't show toast, let InsufficientBalanceModal handle it
          if (error.response?.status === 402) {
            setAutoImporting(false);
            return;
          }

          // Use utility function to safely extract error message
          const errorMessage = extractErrorMessage(error, 'Failed to import Gmail contacts');

          if (errorMessage) {
            present({
              message: errorMessage,
              duration: 3000,
              color: 'danger'
            });
          }

          setAutoImporting(false);
        }
      }
    };

    checkGmailCallback();
  }, []);

  // Client-side filtering based on segment and interest level
  useEffect(() => {
    let filtered = [...allContacts];

    // Filter by segment (recruiters/connections)
    if (segment === 'recruiters') {
      filtered = filtered.filter(contact => contact.type === 'recruiter');
    } else if (segment === 'connections') {
      filtered = filtered.filter(contact => contact.type === 'connection');
    }

    // Filter by interest level if set
    if (filterInterest !== null) {
      filtered = filtered.filter(contact => contact.interest_level === filterInterest);
    }

    setContactsList(filtered);
  }, [allContacts, segment, filterInterest]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText !== undefined) {
        setPage(1); // Reset to first page on new search
        fetchContacts(1, searchText);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  const handleRefresh = (event: CustomEvent) => {
    // Clear cache on manual refresh to get fresh data
    localStorage.removeItem(CONTACTS_CACHE_KEY);
    setTimeout(() => {
      setPage(1);
      fetchContacts(1, searchText);
      event.detail.complete();
    }, 1000);
  };

  const loadMore = async (event: CustomEvent) => {
    if (loadingMore) {
      (event.target as any).complete();
      return;
    }

    const nextPage = page + 1;
    await fetchContacts(nextPage, searchText, true);
    setPage(nextPage);
    (event.target as any).complete();
  };

  const handleContactAdded = () => {
    // Clear cache when new contacts are added
    localStorage.removeItem(CONTACTS_CACHE_KEY);
    setPage(1);
    setHasMore(true); // Reset hasMore when refreshing
    if (scrollRef.current) {
      scrollRef.current.disabled = false; // Re-enable infinite scroll
    }
    fetchContacts(1, searchText);
  };

  const toggleInterestFilter = (level: number) => {
    if (filterInterest === level) {
      setFilterInterest(null);
    } else {
      setFilterInterest(level);
    }
  };

  const renderSkeletons = () => (
    <div>
      {[...Array(3)].map((_, index) => (
        <IonItem key={`skeleton-${index}`}>
          <IonAvatar slot="start">
            <IonSkeletonText animated />
          </IonAvatar>
          <IonLabel>
            <h2><IonSkeletonText animated style={{ width: '70%' }} /></h2>
            <p><IonSkeletonText animated style={{ width: '50%' }} /></p>
            <p><IonSkeletonText animated style={{ width: '40%' }} /></p>
          </IonLabel>
        </IonItem>
      ))}
    </div>
  );

  const renderContactItem = (contact: EnhancedContact) => (
    <IonItem
      key={contact.id}
      button
      detail={true}
      detailIcon={chevronForward}
      routerLink={`/app/network/contact/${contact.id}`}
      className="contact-item"
    >
      <IonAvatar slot="start" className="contact-avatar">
        <div className="avatar-placeholder">
          {contact.name ? contact.name[0].toUpperCase() : '?'}
        </div>
      </IonAvatar>

      <IonLabel>
        <div className="contact-header">
          <h2>{contact.name}</h2>
          <InterestLevel level={contact.interest_level} />
        </div>

        {contact.title && (
          <p className="contact-title">{contact.title}</p>
        )}

        {contact.company && (
          <p className="contact-company">
            <IonIcon icon={businessOutline} />
            <span>{contact.company}</span>
          </p>
        )}

        <div className="contact-metrics">
          <div className="contact-metric">
            <IonIcon icon={timeOutline} />
            <span>{formatDate(contact.last_interaction)}</span>
          </div>

          {contact.total_emails > 0 && (
            <div className="contact-metric">
              <IonIcon icon={mailOutline} />
              <span>{contact.total_emails}</span>
            </div>
          )}

          {contact.response_rate > 0 && (
            <div className="contact-metric">
              <IonIcon icon={mailOpenOutline} />
              <span>{Math.round(contact.response_rate * 100)}%</span>
            </div>
          )}
        </div>

        <div className="contact-tags">
          {contact.type === 'recruiter' && (
            <IonChip className="tag-recruiter">Recruiter</IonChip>
          )}

          {contact.notes && (
            <IonChip className="tag-notes" color="success">Notes</IonChip>
          )}

          {contact.last_interaction && new Date(contact.last_interaction) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) && (
            <IonChip className="tag-recent" color="tertiary">Recent</IonChip>
          )}
        </div>
      </IonLabel>
    </IonItem>
  );

  return (
    <IonPage>
      <IonHeader>
        <TitleBar title="My Network">
          {/* Show individual buttons on larger screens */}
          <IonButton
            onClick={() => setShowFilters(!showFilters)}
            fill={filterInterest !== null ? "solid" : "clear"}
            color={filterInterest !== null ? "primary" : "medium"}
            className="network-filter-btn"
          >
            <IonIcon icon={filterOutline} />
            {filterInterest !== null && (
              <IonBadge color="light">{filterInterest}</IonBadge>
            )}
          </IonButton>
          <IonButton
            onClick={(e) => {
              setPopoverEvent(e.nativeEvent);
              setShowManagePopover(true);
            }}
            className="network-manage-btn"
            size="small"
            style={{
              '--background': '#1e3a8a',
              '--color': 'white',
              height: '36px',
              minWidth: '100px',
              fontSize: '13px',
              fontWeight: '500',
              textTransform: 'none',
            }}
          >
            <IonIcon icon={albumsOutline} slot="start" style={{ fontSize: '16px' }} />
            Manage
          </IonButton>

          {/* Overflow menu for mobile */}
          <IonButton
            onClick={(e) => {
              setPopoverEvent(e.nativeEvent);
              setShowManagePopover(true);
            }}
            fill="clear"
            className="network-overflow-btn"
            style={{ display: 'none' }}
          >
            <IonIcon icon={ellipsisVertical} style={{ fontSize: '24px' }} />
          </IonButton>

          <style>{`
            @media (max-width: 400px) {
              .network-filter-btn,
              .network-manage-btn {
                display: none !important;
              }
              .network-overflow-btn {
                display: inline-flex !important;
              }
            }
          `}</style>
        </TitleBar>

        {/* Manage Menu Popover */}
        <IonPopover
          isOpen={showManagePopover}
          event={popoverEvent}
          onDidDismiss={() => setShowManagePopover(false)}
        >
          <IonContent>
            <IonList style={{
              padding: '8px 0',
              minWidth: '240px',
            }}>
              {/* Filter (mobile only) */}
              <IonItem
                button
                detail={false}
                onClick={() => {
                  setShowFilters(!showFilters);
                  setShowManagePopover(false);
                }}
                lines="none"
                className="mobile-filter-menu-item"
                style={{
                  '--padding-start': '16px',
                  '--padding-end': '16px',
                  '--inner-padding-end': '0',
                  '--min-height': '48px',
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                }}>
                  <IonIcon
                    icon={filterOutline}
                    style={{
                      fontSize: '18px',
                      color: '#666',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#000',
                      lineHeight: '18px',
                      marginBottom: '2px',
                    }}>
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      lineHeight: '16px',
                    }}>
                      Filter by interest level
                    </div>
                  </div>
                  {filterInterest !== null && (
                    <IonBadge color="primary">{filterInterest}</IonBadge>
                  )}
                </div>
              </IonItem>

              {/* Manage Contacts */}
              <IonItem
                button
                detail={false}
                onClick={() => {
                  setImportMode('gmail');
                  setShowImport(true);
                  setShowManagePopover(false);
                }}
                lines="none"
                style={{
                  '--padding-start': '16px',
                  '--padding-end': '16px',
                  '--inner-padding-end': '0',
                  '--min-height': '48px',
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                }}>
                  <IonIcon
                    icon={personAddOutline}
                    style={{
                      fontSize: '18px',
                      color: '#666',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#000',
                      lineHeight: '18px',
                      marginBottom: '2px',
                    }}>
                      Manage Contacts
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      lineHeight: '16px',
                    }}>
                      Import, add, or sync
                    </div>
                  </div>
                </div>
              </IonItem>

              {/* Refresh */}
              <IonItem
                button
                detail={false}
                onClick={() => {
                  localStorage.removeItem(CONTACTS_CACHE_KEY);
                  setPage(1);
                  fetchContacts(1, searchText);
                  setShowManagePopover(false);
                  present({
                    message: 'Contacts refreshed',
                    duration: 1500,
                    color: 'success'
                  });
                }}
                lines="none"
                style={{
                  '--padding-start': '16px',
                  '--padding-end': '16px',
                  '--inner-padding-end': '0',
                  '--min-height': '48px',
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                }}>
                  <IonIcon
                    icon={refreshOutline}
                    style={{
                      fontSize: '18px',
                      color: '#666',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#000',
                      lineHeight: '18px',
                      marginBottom: '2px',
                    }}>
                      Refresh
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      lineHeight: '16px',
                    }}>
                      Reload from server
                    </div>
                  </div>
                </div>
              </IonItem>

              {/* Export */}
              <IonItem
                button
                detail={false}
                onClick={() => {
                  setShowManagePopover(false);
                  present({
                    message: 'Export feature coming soon!',
                    duration: 2000,
                    color: 'medium'
                  });
                }}
                lines="none"
                style={{
                  '--padding-start': '16px',
                  '--padding-end': '16px',
                  '--inner-padding-end': '0',
                  '--min-height': '48px',
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                }}>
                  <IonIcon
                    icon={cloudDownloadOutline}
                    style={{
                      fontSize: '18px',
                      color: '#666',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#000',
                      lineHeight: '18px',
                      marginBottom: '2px',
                    }}>
                      Export Contacts
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      lineHeight: '16px',
                    }}>
                      Download as CSV
                    </div>
                  </div>
                </div>
              </IonItem>
            </IonList>
          </IonContent>
        </IonPopover>

        <IonToolbar>
          <IonSearchbar
            value={searchText}
            onIonInput={e => setSearchText(e.detail.value!)}
            placeholder="Search contacts..."
            className="search-bar"
          />
        </IonToolbar>

        <IonToolbar>
          <IonSegment
            value={segment}
            onIonChange={e => setSegment(e.detail.value!)}
            className="segment-control"
          >
            <IonSegmentButton value="all">ALL</IonSegmentButton>
            <IonSegmentButton value="recruiters">RECRUITERS</IonSegmentButton>
            <IonSegmentButton value="connections">CONNECTIONS</IonSegmentButton>
          </IonSegment>
        </IonToolbar>

        {showFilters && (
          <IonToolbar className="filter-toolbar">
            <div className="interest-filter">
              <span>Interest Level:</span>
              {[2, 4, 6, 8, 10].map(level => (
                <IonChip
                  key={`filter-${level}`}
                  color={filterInterest === level ? "primary" : "medium"}
                  onClick={() => toggleInterestFilter(level)}
                  className="interest-chip"
                >
                  {level / 2}★
                </IonChip>
              ))}
            </div>
          </IonToolbar>
        )}
      </IonHeader>

      <IonContent>
        {/* Auto-import loading overlay */}
        {autoImporting && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <IonSpinner name="crescent" style={{ width: '48px', height: '48px' }} color="light" />
            <IonText color="light" style={{ fontSize: '16px', fontWeight: 500 }}>
              Importing contacts from Gmail...
            </IonText>
          </div>
        )}

        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        {loading && page === 1 ? (
          renderSkeletons()
        ) : contactsList.length === 0 ? (
          <div className="empty-state">
            <p>
              {segment === 'recruiters'
                ? 'Start connecting with recruiters!'
                : segment === 'connections'
                ? 'Build your professional network'
                : 'Let\'s build your network!'}
            </p>
            <IonButton onClick={() => setShowImport(true)}>Import Contacts</IonButton>
          </div>
        ) : (
          <>
            <IonList>
              {contactsList.map(renderContactItem)}
            </IonList>

            {/* Show loading indicator at bottom when loading more */}
            {loadingMore && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '1rem',
                color: 'var(--ion-color-medium)'
              }}>
                <IonSpinner name="bubbles" />
                <span style={{ marginLeft: '0.5rem' }}>Loading more contacts...</span>
              </div>
            )}
          </>
        )}

        <IonInfiniteScroll
          ref={scrollRef}
          threshold="500px"
          disabled={!hasMore || loadingMore}
          onIonInfinite={loadMore}
        >
          <IonInfiniteScrollContent
            loadingSpinner="bubbles"
            loadingText="Loading more contacts..."
          ></IonInfiniteScrollContent>
        </IonInfiniteScroll>

        {/* Manual load button as fallback */}
        {!loading && hasMore && !loadingMore && contactsList.length >= 10 && (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <IonButton
              onClick={() => {
                const fakeEvent = { target: { complete: () => { } } } as any;
                loadMore(fakeEvent);
              }}
              fill="outline"
              color="primary"
              size="small"
            >
              Load More Contacts
            </IonButton>
          </div>
        )}
      </IonContent>

      <Import
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onContactAdded={handleContactAdded}
        gmailConnected={gmailConnected}
        connectedEmail={connectedEmail}
        gmailStatusLoading={gmailStatusLoading}
        onGmailStatusChange={checkGmailStatus}
        initialMode={importMode}
      />
    </IonPage>
  );
};

export default Network;